import { Suspense, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { getAdminNavigation, getAdminNavigationItem } from '@/admin/navigation'
import { ADMIN_CAPABILITIES } from '@/admin/capabilities'
import AdminCommandPalette from './AdminCommandPalette'
import AdminSidebar from './AdminSidebar'
import AdminTopbar from './AdminTopbar'
import ContentSkeleton from '@components/ui/ContentSkeleton'
import ErrorBoundary from '@components/ui/ErrorBoundary'
import {
  useCurrentPageDescription,
  useCurrentPageTitle
} from '@contexts/PageTitleContext'
import { useAdminSession } from '@hooks/useAdminSession'
import { useAdminObservability } from '@hooks/useAdminObservability'

const OBSERVABILITY_WINDOWS = new Set(['15m', '1h', '6h', '24h'])

function getInitialObservabilityWindow(location) {
  if (location.pathname !== '/admin/operations/observability') {
    return '1h'
  }
  const requested = new URLSearchParams(location.search).get('window')
  return OBSERVABILITY_WINDOWS.has(requested) ? requested : '1h'
}

export default function AdminLayout() {
  const { capabilities, session } = useAdminSession()
  const location = useLocation()
  const contextTitle = useCurrentPageTitle()
  const contextDescription = useCurrentPageDescription()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [observabilityWindow, setObservabilityWindow] = useState(() =>
    getInitialObservabilityWindow(location)
  )
  const canReadObservability = (capabilities || []).includes(
    ADMIN_CAPABILITIES.OPERATIONS_READ
  )
  const observability = useAdminObservability(
    observabilityWindow,
    canReadObservability
  )

  const groups = useMemo(() => {
    const summary = observability.data?.summary
    const sources = observability.data?.dataQuality?.sources || []
    const critical =
      Number(summary?.activeCriticalAlerts || 0) +
      Number(summary?.criticalPipelines || 0)
    const attention =
      Number(summary?.activeWarnings || 0) +
      Number(summary?.attentionPipelines || 0)
    const unavailable = Number(summary?.unavailableSources || 0)
    const incompleteEvidence = sources.some(
      (source) => source.status === 'partial' || source.status === 'unavailable'
    )
    const count = critical + attention
    const badge =
      count > 0
        ? count > 99
          ? '99+'
          : String(count)
        : unavailable > 0 || incompleteEvidence
          ? '!'
          : null

    return getAdminNavigation(capabilities).map((group) => ({
      ...group,
      items: group.items.map((item) =>
        item.id === 'operations-observability'
          ? {
              ...item,
              badge,
              badgeTone: critical > 0 ? 'critical' : 'warning'
            }
          : item
      )
    }))
  }, [
    capabilities,
    observability.data?.dataQuality?.sources,
    observability.data?.summary
  ])
  const activeItem = getAdminNavigationItem(location.pathname)
  const title = activeItem?.label || contextTitle || 'Platform Console'
  const description =
    activeItem?.description ||
    contextDescription ||
    'Platform administration and operations'

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileOpen) return undefined
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMobileOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mobileOpen])

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 text-gray-950 dark:bg-gray-950 dark:text-white">
      <a
        href="#admin-main-content"
        className="sr-only z-[120] bg-white px-4 py-2 text-gray-950 focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to console content
      </a>

      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Close Platform Console navigation"
        />
      )}

      <AdminSidebar
        groups={groups}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      <div className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
        <AdminTopbar
          onOpenNavigation={() => setMobileOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
        />

        {session?.environment?.readOnly && (
          <div
            className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
            role="status"
          >
            Platform Console writes are temporarily disabled.
          </div>
        )}

        <main
          id="admin-main-content"
          className="min-w-0 flex-1 overflow-y-auto"
          tabIndex={-1}
        >
          <div className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
            <div className="mx-auto max-w-[1600px] px-4 py-4 sm:px-6 lg:px-8">
              <h1 className="text-xl font-semibold text-gray-950 dark:text-white">
                {title}
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {description}
              </p>
            </div>
          </div>

          <div className="mx-auto max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8">
            <ErrorBoundary>
              <Suspense fallback={<ContentSkeleton />}>
                <Outlet
                  context={{
                    observability,
                    observabilityWindow,
                    setObservabilityWindow
                  }}
                />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <AdminCommandPalette
        groups={groups}
        open={searchOpen}
        onOpen={() => setSearchOpen(true)}
        onClose={() => setSearchOpen(false)}
      />
    </div>
  )
}
